import { useCallback, useEffect, useState } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { StatusOutputsResponse } from '../../../types/statusFactors';

export interface UseStatusOutputsResult {
  data: StatusOutputsResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetches the destinations one sphere feeds. A `null` sphere id, or `enabled: false`, never
 * requests: a sphere that hosts no measure has no upward card, so nothing is asked of the route.
 * A response is only ever returned for the sphere it was fetched for, and a failed reload drops
 * the previous response, so a caller never shows rows the route no longer vouches for.
 */
export function useStatusOutputs(sphereId: string | null | undefined, enabled = true): UseStatusOutputsResult {
  const graphClient = useApiClientsOrNull()?.graphClient;
  const [fetched, setFetched] = useState<{ sphereId: string; response: StatusOutputsResponse } | null>(null);
  const [loading, setLoading] = useState(enabled && Boolean(sphereId) && Boolean(graphClient));
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled || !sphereId || !graphClient) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    graphClient
      .get<StatusOutputsResponse>(`/spheres/${sphereId}/status-outputs`)
      .then((response) => {
        if (!cancelled) setFetched({ sphereId, response });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFetched(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graphClient, sphereId, enabled, reloadToken]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  const data = fetched && fetched.sphereId === sphereId ? fetched.response : null;
  return { data, loading, error, refetch };
}
